import { ShareBaseItemComments, ShareBaseComment } from 'phoenix/domain/sharebase-comment';
import { PhoenixUser } from 'phoenix/domain';
import { CommonUtilsService } from 'util/services/commonUtilsService';
import { IDictionary } from 'util/domain';

interface CommentPost {
    CommentText: string;
}

type TextBounds = {
    start: number;
    end: number;
}

// Using two regex objects for the same pattern.
// The first one is for iterating over a comment to extract all matches.
// Because of the global flag, it seems like it gets messed up when I try
// to just run it once for testing if a string has any matching patterns.
// So I am using a second regex object to do this testing. This seems to
// fix some problems I was having before where some items were not getting
// matched properly.
// https://regex101.com/r/tn4Y4M/1
// Regex should match anything except certain ASCII characters: http://www.asciitable.com/
// The excluded characters are mostly non alphanumeric characters, but I tried to keep those that
// I think are commonly found in names such as hyphens and commas.
const incomingCommentPatternBase = '\\[([^\x00-\x1F\x21-\x2B\x2F\x3C-\x40\x5B-\x60\x7B-\x7F]+)\\]';
const incomingCommentPattern = new RegExp(incomingCommentPatternBase, 'ig');
const incomingCommentPatternTest = new RegExp(incomingCommentPatternBase, 'i');
const outgoingCommentPattern = /\[@User=(\d+)\]([^\x00-\x1F\x21-\x2B\x2F\x3C-\x40\x5B-\x60\x7B-\x7F]+)\[\/\]/ig;
// http, https, and ftp links are valid.
// https://regex101.com/r/Pmm7FS/2
const urlPattern = /https?:\/\/(.(?!(?:[.?!,:](?: |$))))*./i;

export class CommentsService {
    static $inject = [
        '$http',
        '$window',
        'utils'
    ];

    // Maps the comment input mention text to a user.
    // To handle non-unique display names, the value for each user name
    // will be a queue of PhoenixUsers.
    // Later, when the comment text gets processed for the server, the first
    // mention text will map to the first user in the queue because it was
    // the first user put into the queue.
    private mentionsMap:IDictionary<PhoenixUser[]> = {};

    constructor(
        private $http: ng.IHttpService,
        private $window: ng.IWindowService,
        private utils: CommonUtilsService
    ){       
    }

    public postComment(comments:ShareBaseItemComments, commentText:string){
        let post:CommentPost = {
             CommentText: this.toServer(commentText)
        };

        return this.$http.post(comments.Links.AddUri, post)
            .then(resp => resp.data as ShareBaseComment);
    }

    public toHtml(commentContent:string){
        let match;

        // Wrap links in anchor tags; HTML encode anything else
        commentContent = commentContent
            .split(' ')
            .map(word => {
                if (urlPattern.test(word)) {
                    return word.replace(urlPattern, match => {
                        return '<a target="_blank" href=' + match + '>' + match + '</a>';
                    });
                }
                else {
                    return this.utils.htmlEncode(word);
                }
            })
            .join(' ');        
        
        while(match = outgoingCommentPattern.exec(commentContent)) {
            // mention match should come in tuples of 3 (all, id, user name)
            if(match.length % 3 === 0){
                let userName = match[2];
                let html = `<span class="sba-comments-mention-name">${userName}</span>`;

                // replace the original occurence with the new format
                commentContent = commentContent.replace(match[0], html);
            } 
        }
        commentContent = commentContent.replace(/(?:\r\n|\r|\n)/g, '<br />');

        return commentContent;
    }

    public replaceMention(commentText: string, mentionText: string, user: PhoenixUser): string {
        // if there are currently no mentions in the comment text, reset the mentions map
        // to ensure we are not working with stale data.
        if (!incomingCommentPatternTest.exec(commentText)) {
            this.resetMentions();
        }

        let mentionPosition = this.getMentionPosition(commentText, mentionText, user);
        let userMention = this.toMention(user);
        this.addMention(user, mentionPosition);
        return commentText.replace(mentionText, userMention);
    }

    public removeMention(commentText: string, cursorStart: number, cursorEnd: number): void {
        // If cursor start and end are the same, this is a standard delete (i.e. pressing backspace without any selection)
        // decrement the cursor to simulate the resulting delete. If there is selected text,
        // leave the cursor bounds alone.
        if (cursorStart === cursorEnd) {
            cursorEnd = --cursorStart;
        }
        
        // Keep track of found mentions, this way if the mention being deleted is
        // in between others, we don't have to iterate over the text again to
        // find the right position to remove from.
        let foundMatches: IDictionary<number> = {};
        // Flag to kill the loop early once we found a mention to delete
        let stop = false;
        let match: RegExpMatchArray;
        while ((match = incomingCommentPattern.exec(commentText)) && !stop) {
            // Matches should come in tuples of two (all, user name)
            if (match.length % 2 === 0) {
                // First, make sure we actually have a saved mention for the match text.
                // It is possible the user entered text that would match but is not actually
                // a mention.
                let userName = match[1];
                let mentions = this.mentionsMap[userName];
                if (mentions) {
                    let targetMention = match[0];

                    // There are mentions for the matched text. The next test it to see
                    // if the deleted text is within the bounds of the matched mention.
                    // If it is, this is the mention we want to delete and we can stop
                    // the loop. If it is not, track it then continue the loop to the
                    // next iteration.
                    let mentionStart = match.index;
                    let mentionEnd = mentionStart + targetMention.length - 1;

                    if (this.selectionIsInBounds({ start: mentionStart, end: mentionEnd }, { start: cursorStart, end: cursorEnd })) {
                        // Mention was found, remove it and stop the loop
                        stop = true;
                        let pos = foundMatches[targetMention] || 0;
                        this.deleteMention(userName, pos);
                    } else {
                        if (!foundMatches[targetMention]) {
                            foundMatches[targetMention] = 0;
                        }

                        ++foundMatches[targetMention];
                    }
                }
            }
        }
    }

    private toServer(commentContent:string) {      
        let match;

        while (match = incomingCommentPattern.exec(commentContent)) {
            // mention match should come in tuples of 2 (all, user name)
            if (match.length % 2 === 0) {
                let userName = match[1];
                let userQueue = this.mentionsMap[userName];
                let user:PhoenixUser;
                if (userQueue && (user = userQueue.shift())) {
                    let serverMention = `[@User=${user.UserId}]${user.UserName}[/]`;
                    commentContent = commentContent.replace(match[0], serverMention);

                    // If there are no more users for the current map key, clean up the map
                    if (userQueue.length === 0) {
                        this.cleanUpUserQueue(userName);
                    }
                }
            }
        }

        // clear out map so there are no conflicts with next comment
        this.resetMentions();
        return commentContent;
    }

    private toMention(user:PhoenixUser){
        return `[${user.UserName}]`;
    }

    private addMention(user:PhoenixUser, pos?:number) {
        if (!(user.UserName in this.mentionsMap)) {
            this.mentionsMap[user.UserName] = [];
        }

        let userQueue = this.mentionsMap[user.UserName];
        if (!angular.isDefined(pos) || pos === userQueue.length) {
            // no position specified just append to list
            userQueue.push(user);
        } else if (pos === 0) {
            // insert at beginning
            userQueue.unshift(user);
        } else {
            // new user should be inserted to the left of the
            // current user object at pos
            this.mentionsMap[user.UserName] = [
                ...userQueue.slice(0, pos),
                user,
                ...userQueue.slice(pos)
            ];
        }
    }

    private deleteMention(userName:string, pos:number = 0): void {
        if (pos < 0) {
            pos = 0;
        }

        if (!(userName in this.mentionsMap)) {
            return;
        }

        let userQueue = this.mentionsMap[userName];
        if (pos === 0) {
            userQueue.shift();
        } else if (pos >= userQueue.length - 1) {
            userQueue.pop();
        } else {
            this.mentionsMap[userName] = [
                ...userQueue.slice(0, pos),
                ...userQueue.slice(pos + 1)
            ];
        }

        if(userQueue.length < 1){
            this.cleanUpUserQueue(userName);
        }
    }

    private cleanUpUserQueue(userName){
        this.mentionsMap[userName] = null;
        delete this.mentionsMap[userName];
    }

    /**
     * Finds the position in the mention map user queue where this user will be inserted.
     */
    private getMentionPosition(commentText: string, mentionText: string, user: PhoenixUser): number {
        // get index of where the mention will be inserted
        let mentionIndex = commentText.indexOf(mentionText);

        // get the number of similar mentions that come before this one.
        let mentionsBeforeCount = 0;

        let userMention = this.toMention(user);

        let i = commentText.indexOf(userMention);
        while (i >= 0 && i < mentionIndex) {
            ++mentionsBeforeCount;
            i = commentText.indexOf(userMention, i + userMention.length);
        }

        return mentionsBeforeCount;
    }

    private selectionIsInBounds(mention: TextBounds, selection: TextBounds): boolean {
        // Testing if the selected text that will be deleted is within the bounds
        // of the mention text. Either the cursor start or the cursor end must be
        // between the bounds of the mention text.
        return (mention.start <= selection.start && selection.start <= mention.end) ||
            (mention.start <= selection.end && selection.end <= mention.end);
    }

    private resetMentions() {
        this.mentionsMap = {};
    }
}


// WEBPACK FOOTER //
// ./src/sharebase/services/commentsService.ts